import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton as MuiIconButton,
  Stack,
  Paper,
  useTheme,
  alpha,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, ShoppingCart as ShoppingCartIcon, Close as CloseIcon, NavigateNext as NavigateNextIcon, NavigateBefore as NavigateBeforeIcon } from '@mui/icons-material';
import { useTranslation } from '../hooks/useTranslation';
import { Product } from '../types/product';
import { Category, CATEGORY_OPTIONS } from '../types/category';
import api from '../utils/api';
import { ProductType, useCheckout } from '../contexts/CheckoutContext';
import { styled } from '@mui/material/styles';
import type { Theme as MuiTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(2),
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'transform 0.2s, box-shadow 0.2s',
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.08)})`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`,
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.12)})`,
  },
}));

const StyledCardMedia = styled(CardMedia)(({ theme }: { theme: MuiTheme }) => ({
  height: 240,
  position: 'relative',
  '&:hover': {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.2)})`,
    },
  },
}));

const ThumbnailsContainer = styled(Box)(({ theme }: { theme: MuiTheme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  display: 'flex',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.common.black, 0.5),
  backdropFilter: 'blur(4px)',
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    height: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(theme.palette.common.white, 0.3),
    borderRadius: 2,
  },
}));

const Thumbnail = styled(Box)(({ theme }: { theme: MuiTheme }) => ({
  width: 48,
  height: 48,
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  border: '2px solid transparent',
  transition: 'all 0.2s',
  '&:hover': {
    borderColor: theme.palette.common.white,
    transform: 'scale(1.05)',
  },
}));

const PriceQuantityContainer = styled(Box)(({ theme }: { theme: MuiTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5),
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const FinalPrice = styled(Typography)(({ theme }: { theme: MuiTheme }) => ({
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  fontSize: '1.25rem',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const OriginalPrice = styled(Typography)(({ theme }: { theme: MuiTheme }) => ({
  textDecoration: 'line-through',
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}));

const QuantityControls = styled(Box)(({ theme }: { theme: MuiTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  backgroundColor: theme.palette.common.white,
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const DiscountChip = styled(Chip)(({ theme }: { theme: MuiTheme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  transform: 'rotate(-5deg)',
}));

const AddToCartButton = styled(Button)(({ theme }: { theme: MuiTheme }) => ({
  borderRadius: theme.spacing(2),
  textTransform: 'none',
  fontWeight: 'bold',
  padding: theme.spacing(1.5),
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }: { theme: MuiTheme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
  background: `linear-gradient(to bottom, ${alpha(theme.palette.common.white, 0.9)}, ${alpha(theme.palette.common.white, 0.95)})`,
  backdropFilter: 'blur(8px)',
  borderRadius: `0 0 ${theme.spacing(2)} ${theme.spacing(2)}`,
}));

const ProductTitle = styled(Typography)(({ theme }: { theme: MuiTheme }) => ({
  fontWeight: 'bold',
  fontSize: '1.25rem',
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textAlign: 'right',
  [theme.breakpoints.up('sm')]: {
    textAlign: 'right',
  },
}));

const ProductDescription = styled(Typography)(({ theme }: { theme: MuiTheme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  marginBottom: theme.spacing(2),
  lineHeight: 1.5,
  textAlign: 'right',
  [theme.breakpoints.up('sm')]: {
    textAlign: 'right',
  },
}));

const CategoryChip = styled(Chip)(({ theme }: { theme: MuiTheme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.main,
  fontSize: '0.75rem',
  height: '24px',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const ShopTitle = styled(Typography)(({ theme }: { theme: MuiTheme }) => ({
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  textAlign: 'right',
  [theme.breakpoints.up('sm')]: {
    textAlign: 'right',
  },
}));

const ShopSubtitle = styled(Typography)(({ theme }: { theme: MuiTheme }) => ({
  color: theme.palette.text.secondary,
  textAlign: 'right',
  [theme.breakpoints.up('sm')]: {
    textAlign: 'right',
  },
}));

export const ShopPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { addToCheckout } = useCheckout();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<Category | 'all'>('all');
  const [sortBy, setSortBy] = useState('newest');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { currentLang } = useTranslation();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get<{ data: Product[] }>('/products');
      setProducts(response.data.data);
      // Initialize quantities
      const initialQuantities = response.data.data.reduce((acc, product) => {
        acc[product._id] = 1;
        return acc;
      }, {} as Record<string, number>);
      setQuantities(initialQuantities);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (productId: string, value: number) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(1, Math.min(value, 999))
    }));
  };

  const handleAddToCart = async (product: Product) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location } });
      return;
    }

    try {
      await addToCheckout(product._id, ProductType.ITEM, quantities[product._id]);
    } catch (error) {
      console.error('Error adding product to cart:', error);
    }
  };

  const handleImageClick = (product: Product, index: number = 0) => {
    setSelectedProduct(product);
    setCurrentImageIndex(index);
  };

  const handleCloseGallery = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setSelectedProduct(null);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    if (!selectedProduct) return;
    const nextIndex = (currentImageIndex + 1) % (selectedProduct.secondaryImagesPathes.length + 1);
    setCurrentImageIndex(nextIndex);
  };

  const handlePrevImage = () => {
    if (!selectedProduct) return;
    const prevIndex = currentImageIndex === 0 
      ? selectedProduct.secondaryImagesPathes.length 
      : currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
  };

  const filteredProducts = products.filter(product => 
    selectedCategory === 'all' || product.category === selectedCategory
  );

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'priceLow':
        return a.price - b.price;
      case 'priceHigh':
        return b.price - a.price;
      case 'newest':
        return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.1)})`,
        }}
      >
        <ShopTitle 
          variant="h4" 
          gutterBottom
          sx={{ 
            textAlign: currentLang === 'he' ? 'right' : 'left',
            [theme.breakpoints.up('sm')]: {
              textAlign: currentLang === 'he' ? 'right' : 'left',
            }
          }}
        >
          {t('pages.shop.title')}
        </ShopTitle>
        <ShopSubtitle 
          variant="subtitle1" 
          paragraph
          sx={{ 
            textAlign: currentLang === 'he' ? 'right' : 'left',
            [theme.breakpoints.up('sm')]: {
              textAlign: currentLang === 'he' ? 'right' : 'left',
            }
          }}
        >
          {t('pages.shop.subtitle')}
        </ShopSubtitle>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('pages.shop.filters.category')}</InputLabel>
              <Select
                value={selectedCategory}
                label={t('pages.shop.filters.category')}
                onChange={(e) => setSelectedCategory(e.target.value as Category | 'all')}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">{t('pages.shop.categories.all')}</MenuItem>
                {CATEGORY_OPTIONS.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {t(`admin.products.categories.${category.value.toLowerCase()}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('pages.shop.filters.sortBy')}</InputLabel>
              <Select
                value={sortBy}
                label={t('pages.shop.filters.sortBy')}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="newest">{t('pages.shop.sort.newest')}</MenuItem>
                <MenuItem value="priceLow">{t('pages.shop.sort.priceLow')}</MenuItem>
                <MenuItem value="priceHigh">{t('pages.shop.sort.priceHigh')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Grid 
        container 
        spacing={3}
        direction={currentLang === 'he' ? 'row-reverse' : 'row'}
      >
        {sortedProducts.map((product) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
            <StyledCard>
              <Box sx={{ position: 'relative' }}>
                <StyledCardMedia
                  image={product.imagePath}
                  title={product.title}
                  onClick={() => handleImageClick(product)}
                />
                {product.secondaryImagesPathes.length > 0 && (
                  <ThumbnailsContainer>
                    {product.secondaryImagesPathes.map((image, index) => (
                      <Thumbnail key={index}>
                        <img
                          src={image}
                          alt={`${product.title} - Image ${index + 2}`}
                          style={{ width: '100%', height: '100%', objectFit: 'scale-down' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick(product, index + 1);
                          }}
                        />
                      </Thumbnail>
                    ))}
                  </ThumbnailsContainer>
                )}
              </Box>
              <StyledCardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <CategoryChip
                    label={t(`admin.products.categories.${product.category.toLowerCase()}`)}
                    size="small"
                  />
                  <ProductTitle>{product.title}</ProductTitle>
                </Box>
                <ProductDescription>{product.description}</ProductDescription>
                <PriceQuantityContainer>
                  <FinalPrice>
                    {product.discount > 0 ? (
                      <>
                        <OriginalPrice>₪{product.price}</OriginalPrice>
                        ₪{product.price * (1 - product.discount / 100)}
                        <DiscountChip
                          label={`${product.discount}% OFF`}
                          size="small"
                        />
                      </>
                    ) : (
                      `₪${product.price}`
                    )}
                  </FinalPrice>
                  <QuantityControls>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(product._id, quantities[product._id] - 1);
                      }}
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': { 
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          transform: 'scale(1.1)',
                        }
                      }}
                    >
                      <RemoveIcon />
                    </IconButton>
                    <TextField
                      size="small"
                      type="number"
                      value={quantities[product._id]}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(product._id, parseInt(e.target.value));
                      }}
                      inputProps={{ 
                        min: 1, 
                        max: 999,
                        style: { 
                          textAlign: 'center',
                          padding: '4px 0',
                          width: '40px'
                        }
                      }}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          '&:hover fieldset': { border: 'none' },
                          '&.Mui-focused fieldset': { border: 'none' },
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(product._id, quantities[product._id] + 1);
                      }}
                      sx={{ 
                        color: 'primary.main',
                        '&:hover': { 
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          transform: 'scale(1.1)',
                        }
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </QuantityControls>
                </PriceQuantityContainer>
                <AddToCartButton
                  variant="contained"
                  fullWidth
                  startIcon={<ShoppingCartIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCart(product);
                  }}
                >
                  {t('pages.shop.addToCart')}
                </AddToCartButton>
              </StyledCardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={!!selectedProduct}
        onClose={handleCloseGallery}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <MuiIconButton
            onClick={handleCloseGallery}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 1,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <CloseIcon />
          </MuiIconButton>
          
          {selectedProduct && (
            <Box sx={{ position: 'relative' }}>
              <img
                src={currentImageIndex === 0 
                  ? selectedProduct.imagePath 
                  : selectedProduct.secondaryImagesPathes[currentImageIndex - 1]}
                alt={selectedProduct.title}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '80vh',
                  objectFit: 'contain' as const
                }}
              />
              
              {selectedProduct.secondaryImagesPathes.length > 0 && (
                <>
                  <MuiIconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)'
                      }
                    }}
                  >
                    <NavigateBeforeIcon />
                  </MuiIconButton>
                  
                  <MuiIconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)'
                      }
                    }}
                  >
                    <NavigateNextIcon />
                  </MuiIconButton>
                </>
              )}
              
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  padding: '4px 8px',
                  borderRadius: '16px'
                }}
              >
                {[selectedProduct.imagePath, ...selectedProduct.secondaryImagesPathes].map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: currentImageIndex === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(index);
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}; 